/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Theme, useMediaQuery } from "@mui/material";
import React, { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { PageContainer } from "../components/layout/PageContainer";
import Section from "../components/layout/Section";
import { Loader, LoadingBox } from "../components/Loader";
import Tabs, { TabPanel } from "../components/Tabs";
import TitleBox from "../components/TitleBox";
import { selectCurrentFarmerAccount } from "../features/azure-b2c/storage";
import { Declarations } from "../features/declarations/components/DeclarationsButton";
import ActiveAgreementsTabPanel from "../features/sales-agreements/ActiveAgreements";
import CampaignModal from "../features/sales-agreements/components/CampaignModal/CampaignModal";
import NoSalesAgreement from "../features/sales-agreements/NoSalesAgreement";
import PastAgreementsTabPanel from "../features/sales-agreements/PastAgreements";
import {
  getSalesAgreements,
  selectActiveSalesAgreements,
  selectAgreementsRequestStatus,
  selectAllSalesAgreements,
  selectPastSalesAgreements,
  selectUpcomingSalesAgreements,
} from "../features/sales-agreements/storage";
import { AgreementsCampaignsAtomic } from "../services/atomic/AgreementsCampaignsAtomic";
import { useAppDispatch } from "../store/rootStore";
import { TestIds } from "../testIds";
import { CONTACT_CARD_TITLE, CONTENT_LAYOUT_WIDTH } from "../util/consts";
import { useScrollRestoration } from "../util/useScrollRestoration";

export function SalesAgreements() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestStatus = useSelector(selectAgreementsRequestStatus);
  const selectedAccount = useSelector(selectCurrentFarmerAccount);
  const salesAgreements = useSelector(selectAllSalesAgreements);
  const pastAgreements = useSelector(selectPastSalesAgreements);
  const activeAgreements = useSelector(selectActiveSalesAgreements);
  const upcomingAgreements = useSelector(selectUpcomingSalesAgreements);
  const ref = useRef<HTMLElement>(null);
  useScrollRestoration({
    ref,
    scrollYPositionKey: sessionStorage.scrollYPositionOnAgreements,
    path: "/agreement/",
  });

  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const tab = parseInt(searchParams.get("tab") ?? "0", 10);
  const setTab: React.Dispatch<React.SetStateAction<number>> = (next) => {
    const value = typeof next === "function" ? next(tab) : next;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", value.toString());
      return params;
    });
  };

  const pastAvailableOnly = useMemo(() => {
    return (
      pastAgreements?.length !== 0 &&
      activeAgreements?.length === 0 &&
      upcomingAgreements?.length === 0
    );
  }, [pastAgreements, activeAgreements, upcomingAgreements]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatchGetSalesAgreements();
  }, [selectedAccount]);

  const dispatchGetSalesAgreements = () => {
    if (selectedAccount) {
      dispatch(getSalesAgreements(selectedAccount.accountNumber));
    }
  };

  const handleCampaignModalClosed = () => {
    // Closing the modal shouldn't add a history entry, so replace the current one.
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.delete("campaignId");
        return params;
      },
      { replace: true }
    );
  };

  const tabsInfo = [
    {
      label: "Active agreements",
      "data-testid": TestIds.feat.agreement.tabView.activeAgreements,
      details: (
        <ActiveAgreementsTabPanel
          activeAgreements={activeAgreements}
          upcomingAgreements={upcomingAgreements}
        />
      ),
      default: !pastAvailableOnly,
    },
    {
      label: "Closed agreements",
      "data-testid": TestIds.feat.agreement.tabView.pastAgreements,
      details: <PastAgreementsTabPanel pastAgreements={pastAgreements} />,
      default: pastAvailableOnly,
    },
  ];

  const tabPanels = React.useMemo(() => {
    return (
      <>
        <Section width={"100%"}>
          {isMd && (
            <Box position="absolute" right="0px">
              <Declarations />
            </Box>
          )}
          <TitleBox display="Sales Agreements" />

          <AgreementsCampaignsAtomic />

          <Tabs
            value={tab}
            setValue={setTab}
            tabsInfo={tabsInfo}
            aria-label="sales agreement tabs"
            id="sales-agreement"
          />
        </Section>
        {tabsInfo.map((x, index) => (
          <TabPanel key={`${x.label}-${tab}`} value={tab} index={index} id={"sales-agreement"}>
            {x.details}
          </TabPanel>
        ))}
      </>
    );
  }, [salesAgreements, tab, isMd]);

  const memo = React.useMemo(() => {
    if (salesAgreements.length === 0 && requestStatus === "pending") {
      return (
        <>
          <TitleBox display="Sales Agreements" />
          <LoadingBox>
            <Loader title="Loading sales agreements" />
          </LoadingBox>
        </>
      );
    } else if (salesAgreements && Object.keys(salesAgreements).length > 0) {
      return <>{tabPanels}</>;
    } else {
      return (
        <>
          <Section width={"100%"} mb={2}>
            {isMd && (
              <Box position="absolute" right="0px">
                <Declarations />
              </Box>
            )}
            <TitleBox display="Sales Agreements" />

            <AgreementsCampaignsAtomic />
            <Tabs
              value={tab}
              setValue={setTab}
              tabsInfo={tabsInfo}
              aria-label="sales agreement tabs"
              id="sales-agreement"
            />
          </Section>

          <NoSalesAgreement
            display=""
            body="You do not have any Sales Agreements associated with this account. If you would like to know more about Sales Agreements at Farmlands or set up a contract for product please contact your Farmlands sales representative. "
            contactTitle={CONTACT_CARD_TITLE.noAgreements}
          />
        </>
      );
    }
  }, [requestStatus, selectedAccount, salesAgreements, tabPanels]);

  return (
    <PageContainer
      data-testid={TestIds.pages.salesAgreements}
      sx={{ maxWidth: `${CONTENT_LAYOUT_WIDTH}px` }}
    >
      {memo}
      <CampaignModal
        campaignId={searchParams.get("campaignId")}
        onModalClosed={handleCampaignModalClosed}
      />
    </PageContainer>
  );
}

export default SalesAgreements;
